import {
  getClosestCurrencyExchangeRatesToDate,
  getExchangeRate,
} from '../api/currency-exchange.api';
import { useEffectiveExchangeRates } from '@/api/agency-exchange-rates.api';
import { useGetMe } from '@/api/user.api';
import { queryKeys } from '@/queryKeys';
import { UserType } from '@/types/auth/UserType';
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

const useGetLatestExchangeRates = () =>
  useQuery({
    queryKey: queryKeys.currency.latestRates,
    queryFn: getExchangeRate,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

const useGetClosestExchangeRatesToDate = (date?: Date) =>
  useQuery({
    queryKey: queryKeys.currency.closestRates(date),
    queryFn: () =>
      date
        ? getClosestCurrencyExchangeRatesToDate(date)
        : Promise.reject('No date provided'),
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled: Boolean(date),
  });

export const useConvertPrice = (date?: Date) => {
  const { data: userData } = useGetMe();
  const isDistributor = userData?.userType === UserType.Distributor;

  const { data: latestExchangeRates } = useGetLatestExchangeRates();
  const { data: closestExchangeRates } = useGetClosestExchangeRatesToDate(date);
  const { data: effectiveRates } = useEffectiveExchangeRates(
    date,
    isDistributor,
  );

  const exchangeRates = isDistributor
    ? effectiveRates
    : date
      ? closestExchangeRates
      : latestExchangeRates;

  const convertPrice = useCallback(
    (price: number, fromCurrency: string, toCurrency: string) => {
      if (!exchangeRates?.rates) return price;
      const rates = exchangeRates.rates;
      if (!rates[fromCurrency] || !rates[toCurrency]) return price;
      const conversionRate = rates[toCurrency] / rates[fromCurrency];
      return price * conversionRate;
    },
    [exchangeRates],
  );

  return { convertPrice };
};

export const useConvertPriceOnlyIfDateISProvided = (date?: Date) => {
  const { convertPrice } = useConvertPrice(date);

  return useCallback(
    (price: number, fromCurrency: string, toCurrency: string) => {
      if (!date) return price;
      return convertPrice(price, fromCurrency, toCurrency);
    },
    [convertPrice, date],
  );
};
